import React from "react"
import { View, StyleSheet, TouchableOpacity, Image } from "react-native"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"

/**
 * Carte-réponse type "radio" pour le template de question funnel (pattern Preply).
 * Une ligne : label (+ sous-label optionnel + emoji ou icône-image optionnels) à
 * gauche, pastille radio à droite. État sélectionné = bordure + fond primaire
 * clair, pastille pleine. Mono ou multi-sélection au choix de l'appelant (la
 * carte ne connaît que `selected` / `onPress`).
 *
 * Texte rendu via TextM (design system) ; la pastille est dessinée en View pour
 * un rendu identique web/natif (pas de dépendance icône). `iconUri` permet
 * d'afficher une icône-image distante (ex. icônes de matières renvoyées par
 * l'API) ; `emoji` reste prioritaire pour les options statiques.
 */
const AnswerCardM = ({
    label,
    subLabel,
    emoji,
    iconUri,
    selected,
    onPress,
    disabled = false,
    multi = false,
    style,
}) => {
    return (
        <TouchableOpacity
            activeOpacity={0.85}
            onPress={disabled ? undefined : onPress}
            style={[
                styles.card,
                selected && styles.cardSelected,
                disabled && styles.cardDisabled,
                style,
            ]}
        >
            {emoji ? (
                <TextM style={styles.emoji}>{emoji}</TextM>
            ) : iconUri ? (
                <Image
                    source={{ uri: iconUri }}
                    style={styles.iconImage}
                    resizeMode="contain"
                />
            ) : null}
            <View style={styles.textCol}>
                <TextM
                    wrap
                    fontWeight={selected ? "Bold" : "SemiBold"}
                    fontSize={15}
                    color={selected ? theme.colors.primary : theme.colors.grey}
                >
                    {label}
                </TextM>
                {subLabel ? (
                    <TextM
                        wrap
                        fontSize={12}
                        color={theme.colors.light}
                        style={{ marginTop: 2 }}
                    >
                        {subLabel}
                    </TextM>
                ) : null}
            </View>
            <View
                style={[
                    multi ? styles.checkbox : styles.radio,
                    selected && styles.indicatorSelected,
                ]}
            >
                {selected ? (
                    multi ? (
                        <TextM style={styles.checkMark}>✓</TextM>
                    ) : (
                        <View style={styles.radioDot} />
                    )
                ) : null}
            </View>
        </TouchableOpacity>
    )
}

const styles = StyleSheet.create({
    card: {
        flexDirection: "row",
        alignItems: "center",
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        borderRadius: 14,
        paddingVertical: 16,
        paddingHorizontal: 16,
        backgroundColor: theme.colors.white,
        // Légère ombre portée (web + iOS) pour le relief "carte propre" du modèle.
        shadowColor: "#000",
        shadowOpacity: 0.04,
        shadowRadius: 6,
        shadowOffset: { width: 0, height: 2 },
    },
    cardSelected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    cardDisabled: { opacity: 0.4 },
    emoji: { fontSize: 20, marginRight: 12 },
    iconImage: { width: 24, height: 24, marginRight: 12 },
    textCol: { flex: 1, paddingRight: 12 },
    radio: {
        width: 24,
        height: 24,
        borderRadius: 12,
        borderWidth: 2,
        borderColor: theme.colors.lightMore,
        alignItems: "center",
        justifyContent: "center",
    },
    checkbox: {
        width: 24,
        height: 24,
        borderRadius: 7,
        borderWidth: 2,
        borderColor: theme.colors.lightMore,
        alignItems: "center",
        justifyContent: "center",
    },
    indicatorSelected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primary,
    },
    radioDot: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: theme.colors.white,
    },
    checkMark: {
        color: theme.colors.white,
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
    },
})

export default AnswerCardM
